import React from 'react'
import { Row, H1, LabelSpacer, Button, DialogOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from '@fs/zion-router'
import { css, type SerializedStyles } from '@emotion/core'
import PlaceReportsBreadCrumbs from './PlaceReportsBreadCrumbs'
import PlaceReportsSearch from './PlaceReportsSearch'
import PlaceReportsGuide from './PlaceReportsGuide'
import PlaceReportsTypeThesaurus from './PlaceReportsTypeThesaurus'
import { usePlaceReportsData } from '../../PlaceReportsProvider'

const tabStyles = (isActive: boolean, isDisabled: boolean): SerializedStyles => {
  let backgroundColor: string
  if (isActive) {
    backgroundColor = '#0051C3'
  } else if (isDisabled) {
    backgroundColor = '#f0f0f0'
  } else {
    backgroundColor = '#e0e0e0'
  }

  let textColor: string
  if (isActive) {
    textColor = '#ffffff'
  } else if (isDisabled) {
    textColor = '#a0a0a0'
  } else {
    textColor = '#333333'
  }

  let hoverBackgroundColor: string
  if (isDisabled) {
    hoverBackgroundColor = '#f0f0f0'
  } else if (isActive) {
    hoverBackgroundColor = '#0051C3'
  } else {
    hoverBackgroundColor = '#d0d0d0'
  }

  return css`
    padding: 8px 24px 8px 24px;
    margin: 0;
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
    background-color: ${backgroundColor};
    color: ${textColor};
    border: none;
    border-start-start-radius: 4px;
    border-start-end-radius: 4px;
    border-end-start-radius: 0;
    border-end-end-radius: 0;
    font-size: 14px;
    font-weight: ${isActive ? '600' : '400'};
    transition: all 0.2s ease;
    opacity: ${isDisabled ? '0.6' : '1'};
    display: inline-block;
    line-height: 1;

    &:hover {
      background-color: ${hoverBackgroundColor};
    }
  `
}

export default function PlaceReportsHeader(): JSX.Element {
  const [t] = useTranslation()
  const guideOverlay = useOverlay()
  const thesaurusOverlay = useOverlay()
  const history = useHistory()
  const location = useLocation()
  const { viewType, setViewType, hasScorecardAvailable } = usePlaceReportsData()

  const updateViewInURL = (newViewType: 'guidelines' | 'scorecard'): void => {
    const searchParams = new URLSearchParams(location.search)

    if (newViewType === 'scorecard') {
      searchParams.set('view', 'scorecard')
    } else {
      searchParams.delete('view')
    }

    const newSearch = searchParams.toString()
    const queryString = newSearch ? `?${newSearch}` : ''
    history.push(`${location.pathname}${queryString}`)
    setViewType(newViewType)
  }

  return (
    <div
      style={{ marginInlineStart: '8px', marginInlineEnd: '8px', marginTop: '8px', marginBottom: 0, paddingBottom: 0 }}
    >
      <Row alignItemsY="middle" wrap>
        <Row growX noShrinkX>
          <div growX noShrinkX>
            <H1 size="H3">{t('place-reports.reports-header.header', 'Place Reports')}</H1>
            <PlaceReportsBreadCrumbs />
          </div>
        </Row>
        <Row alignItemsY="middle" gutters="xs">
          <div style={{ border: '1px solid #d0d0d0', borderRadius: '4px', paddingLeft: '8px', paddingRight: '8px' }}>
            <Button
              onClick={thesaurusOverlay.handleClick}
              title="Reference guide for place types and their definitions"
            >
              {t('place-reports.type-thesaurus.button', 'Type Thesaurus')}
            </Button>
          </div>
          <div style={{ border: '1px solid #d0d0d0', borderRadius: '4px', paddingLeft: '8px', paddingRight: '8px' }}>
            <Button
              onClick={guideOverlay.handleClick}
              title="Detailed user guide for place report sections and defaults"
            >
              {t('place-reports.guide.button', 'User Guide')}
            </Button>
          </div>
          <div style={{ width: 400 }}>
            <LabelSpacer />
            <PlaceReportsSearch />
          </div>
        </Row>
      </Row>

      <div
        role="tablist"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '4px',
          marginBottom: 0,
          paddingBottom: 0,
          gap: '2px',
        }}
      >
        <button
          role="tab"
          aria-selected={viewType === 'guidelines'}
          css={tabStyles(viewType === 'guidelines', false)}
          onClick={() => updateViewInURL('guidelines')}
          type="button"
        >
          {t('place-reports.header.place-report', 'Place Report')}
        </button>
        <button
          role="tab"
          aria-selected={viewType === 'scorecard'}
          aria-disabled={!hasScorecardAvailable}
          css={tabStyles(viewType === 'scorecard', !hasScorecardAvailable)}
          onClick={() => (hasScorecardAvailable ? updateViewInURL('scorecard') : null)}
          disabled={!hasScorecardAvailable}
          title={
            hasScorecardAvailable
              ? ''
              : t('place-reports.header.scorecard-unavailable', 'Scorecard not available for this place')
          }
          type="button"
        >
          {t('place-reports.header.scorecard', 'Scorecard')}
        </button>
      </div>

      <DialogOverlay title={t('place-reports.guide.title', 'Place Reports User Guide')} size="xl" {...guideOverlay}>
        <PlaceReportsGuide />
      </DialogOverlay>

      <DialogOverlay
        title={t('place-reports.type-thesaurus.title', 'Place Type Thesaurus')}
        size="xl"
        {...thesaurusOverlay}
      >
        <PlaceReportsTypeThesaurus />
      </DialogOverlay>
    </div>
  )
}
